// Native scrollbars are hidden app-wide (app.css) so they never take layout
// width; this draws a phone-style overlay thumb for whatever element scrolls.
// Scroll events don't bubble but do capture, so one document listener finds
// every scroller — components carry no wiring.
import {
  createVisibility,
  fadeMs,
  scrollPerThumbPx,
  thumbGeometry,
  type AxisMetrics,
  type Visibility,
} from './scrollThumb';

type Axis = 'y' | 'x';

interface Bar {
  axis: Axis;
  rail: HTMLDivElement;
  thumb: HTMLDivElement;
  track: number;
}

interface Entry {
  el: Element;
  bars: Bar[];
  vis: Visibility;
  ro: ResizeObserver;
  shown: boolean;
  frame: number;
}

const RAIL = 12;
const INSET = 2;
const PAGE = 0.875;

const entries = new WeakMap<Element, Entry>();
const live = new Set<Entry>();
const layers = new WeakMap<Element, HTMLElement>();

const root = (): Element => document.scrollingElement ?? document.documentElement;

const metrics = (el: Element, axis: Axis): AxisMetrics =>
  axis === 'y'
    ? { position: el.scrollTop, content: el.scrollHeight, viewport: el.clientHeight }
    : { position: el.scrollLeft, content: el.scrollWidth, viewport: el.clientWidth };

function scrolls(el: Element, axis: Axis): boolean {
  const m = metrics(el, axis);
  if (m.content - m.viewport < 1) return false;
  const prop = axis === 'y' ? 'overflowY' : 'overflowX';
  if (el === root()) {
    return ![el, document.body].some((e) => /hidden|clip/.test(getComputedStyle(e)[prop]));
  }
  return /auto|scroll/.test(getComputedStyle(el)[prop]);
}

const scrollable = (el: Element) => scrolls(el, 'y') || scrolls(el, 'x');

function scrollerAt(target: Element | null): Element | null {
  for (let el = target; el && el !== root(); el = el.parentElement) {
    if (scrollable(el)) return el;
  }
  return scrollable(root()) ? root() : null;
}

// Top-layer elements (modal dialogs, popovers) paint over anything in body, so
// a scroller inside one gets its own overlay layer inside it.
function layerFor(el: Element): HTMLElement {
  const host = (el === root() ? null : el.closest('dialog, [popover]')) ?? document.body;
  let layer = layers.get(host);
  if (!layer?.isConnected) {
    layer = document.createElement('div');
    layer.setAttribute('data-scroll-overlay', '');
    layer.setAttribute('aria-hidden', 'true');
    host.append(layer);
    layers.set(host, layer);
  }
  return layer;
}

function viewportBox(el: Element) {
  if (el === root()) return { left: 0, top: 0, width: el.clientWidth, height: el.clientHeight };
  const r = el.getBoundingClientRect();
  return {
    left: r.left + el.clientLeft,
    top: r.top + el.clientTop,
    width: el.clientWidth,
    height: el.clientHeight,
  };
}

function layout(e: Entry): void {
  if (!e.el.isConnected) {
    e.vis.dispose();
    entries.delete(e.el);
    return hide(e);
  }
  const box = viewportBox(e.el);
  const host = e.bars[0].rail.parentElement?.getBoundingClientRect() ?? { left: 0, top: 0 };
  for (const bar of e.bars) {
    const y = bar.axis === 'y';
    bar.track = (y ? box.height : box.width) - 2 * INSET;
    const g = scrolls(e.el, bar.axis) ? thumbGeometry(metrics(e.el, bar.axis), bar.track) : null;
    bar.rail.hidden = !g;
    if (!g) continue;
    const s = bar.rail.style;
    s.left = `${(y ? box.left + box.width - RAIL : box.left + INSET) - host.left}px`;
    s.top = `${(y ? box.top + INSET : box.top + box.height - RAIL) - host.top}px`;
    s.width = `${y ? RAIL : bar.track}px`;
    s.height = `${y ? bar.track : RAIL}px`;
    bar.thumb.style[y ? 'height' : 'width'] = `${g.size}px`;
    bar.thumb.style.transform = y ? `translateY(${g.offset}px)` : `translateX(${g.offset}px)`;
  }
}

function schedule(e: Entry): void {
  e.frame ||= requestAnimationFrame(() => {
    e.frame = 0;
    if (e.shown) layout(e);
  });
}

const reducedMotion = () => matchMedia('(prefers-reduced-motion: reduce)').matches;

function show(e: Entry): void {
  e.shown = true;
  live.add(e);
  e.ro.observe(e.el);
  const layer = layerFor(e.el);
  const fade = fadeMs(reducedMotion());
  for (const { rail } of e.bars) {
    rail.style.transition = fade ? `opacity ${fade}ms ease-out` : 'none';
    if (rail.parentElement !== layer) layer.append(rail);
  }
  layout(e);
  // Commit the hidden state first so the opacity change animates.
  void e.bars[0].rail.offsetWidth;
  for (const { rail } of e.bars) rail.setAttribute('data-shown', '');
}

function hide(e: Entry): void {
  e.shown = false;
  live.delete(e);
  e.ro.disconnect();
  for (const { rail } of e.bars) rail.removeAttribute('data-shown');
  setTimeout(
    () => {
      if (!e.shown) for (const { rail } of e.bars) rail.remove();
    },
    fadeMs(reducedMotion()),
  );
}

function makeBar(e: Entry, axis: Axis): Bar {
  const rail = document.createElement('div');
  const thumb = document.createElement('div');
  rail.className = 'scroll-rail';
  rail.dataset.axis = axis;
  thumb.className = 'scroll-thumb';
  rail.append(thumb);
  const bar: Bar = { axis, rail, thumb, track: 0 };
  const y = axis === 'y';
  const scrollTo = (v: number) => (y ? (e.el.scrollTop = v) : (e.el.scrollLeft = v));

  rail.addEventListener('pointerenter', () => e.vis.hold(true));
  rail.addEventListener('pointerleave', () => {
    if (!rail.hasAttribute('data-drag')) e.vis.hold(false);
  });
  rail.addEventListener('pointerdown', (ev) => {
    if (ev.button !== 0) return;
    ev.preventDefault();
    const at = y ? ev.clientY : ev.clientX;
    if (ev.target === rail) {
      const t = thumb.getBoundingClientRect();
      const sign = at < (y ? t.top : t.left) ? -1 : 1;
      const page = (y ? e.el.clientHeight : e.el.clientWidth) * PAGE * sign;
      e.el.scrollBy(y ? { top: page } : { left: page });
      return;
    }
    const m = metrics(e.el, axis);
    const ratio = scrollPerThumbPx(m, bar.track);
    rail.setPointerCapture(ev.pointerId);
    rail.setAttribute('data-drag', '');
    const move = (mv: PointerEvent) => scrollTo(m.position + ((y ? mv.clientY : mv.clientX) - at) * ratio);
    const end = () => {
      rail.removeEventListener('pointermove', move);
      rail.removeAttribute('data-drag');
      e.vis.hold(rail.matches(':hover'));
    };
    rail.addEventListener('pointermove', move);
    rail.addEventListener('lostpointercapture', end, { once: true });
  });
  return bar;
}

function entryFor(el: Element): Entry {
  let e = entries.get(el);
  if (!e) {
    const entry = { el, shown: false, frame: 0 } as Entry;
    entry.vis = createVisibility((v) => (v ? show(entry) : hide(entry)));
    entry.ro = new ResizeObserver(() => schedule(entry));
    entry.bars = [makeBar(entry, 'y'), makeBar(entry, 'x')];
    entries.set(el, (e = entry));
  }
  return e;
}

/** Installs the app-wide overlay scrollbars; returns the teardown. */
export function installOverlayScrollbars(): () => void {
  if (typeof ResizeObserver === 'undefined' || !window.matchMedia) return () => {};
  const fine = matchMedia('(pointer: fine)');
  const poke = (el: Element) => {
    const e = entryFor(el);
    e.vis.poke();
    schedule(e);
  };

  let lastTarget: EventTarget | null = null;
  let lastScroller: Element | null = null;
  const onScroll = (ev: Event) => {
    if (!fine.matches) return;
    lastTarget = null;
    // A scrolled ancestor moves every visible thumb inside it.
    for (const e of live) schedule(e);
    const el = ev.target === document ? root() : (ev.target as Element);
    if (scrollable(el)) poke(el);
  };

  const onPointer = (ev: PointerEvent) => {
    if (!fine.matches || ev.pointerType === 'touch') return;
    const target = ev.target as Element;
    if (target.closest?.('[data-scroll-overlay]')) return;
    if (target !== lastTarget) {
      lastTarget = target;
      lastScroller = scrollerAt(target);
    }
    if (lastScroller) poke(lastScroller);
  };

  const opts = { capture: true, passive: true };
  document.addEventListener('scroll', onScroll, opts);
  document.addEventListener('pointermove', onPointer, opts);
  return () => {
    document.removeEventListener('scroll', onScroll, opts);
    document.removeEventListener('pointermove', onPointer, opts);
    for (const e of live) {
      e.vis.dispose();
      hide(e);
    }
  };
}
